import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { ROUTES } from "../../route_paths";

interface UseResetPasswordResult {
  password: string;
  setPassword: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
  error: string | null;
  loading: boolean;
  invalidLink: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

/** Handles the Supabase recovery flow. Redirects to / after a successful password update. */
export function useResetPassword(): UseResetPasswordResult {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // True when the page is reached without a valid recovery token in the URL.
  const [invalidLink, setInvalidLink] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase appends #access_token=...&type=recovery to the redirect URL.
    // If the type fragment is absent the link is invalid or already used.
    if (!window.location.hash.includes("type=recovery")) {
      setInvalidLink(true);
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      await supabase.auth.signOut();
      navigate(ROUTES.landing.path(), { replace: true });
    } finally {
      setLoading(false);
    }
  }

  return { password, setPassword, confirm, setConfirm, error, loading, invalidLink, handleSubmit };
}
